// Assurance-Case Assembler (B1)
//
// A PURE, read-time assembler that composes — per control — a ControlAssuranceCase:
// an auditor's argument in Goal Structuring Notation shape.
//
//   Goal  ──▶ Strategy ──▶ four sub-claims ──▶ evidence nodes
//
//     { controlId, goal, verdict, strategy,
//       subClaims: [ { key, label, verdict, evidence: [<A1/A2/A3 nodes>], … } ],
//       weakestLink, predictedWeakLinks }
//
// It ASSEMBLES the leaves the platform already computes — it does not recompute
// them. The six compliance gates (evaluateControl), the evidence nodes with their
// state/confidence/predicted dimensions (A1 evidenceNodes.js, A2 nodeClassification.js,
// A3 nodePrediction.js), the obligation currency, and the effectiveness rating are
// all read as-is and threaded into a stated argument. Nothing is stored.
//
// ── Mapping gates → the four lifecycle sub-claims ─────────────────────────────
//   designed    = Gate 1 (document exists)     + Gate 2 (review within SLA)
//   implemented = Gate 4 (implementation evidence) + Gate 6 (ownership, when enabled)
//   operating   = Gate 5 (obligations current)  + Gate 3 (readers acknowledged)
//                 + the ISO 27001:2022 §9.1 effectiveness rating
//   monitored   = integration-freshness derivations + engine_run_log presence
//                 (continuous-monitoring proof — referenced, never rebuilt here)
//
// Sub-claim VERDICTS roll up from the gates each sub-claim owns (respecting the
// existing pass/blocked semantics and the hard "overdue obligations ⇒ non-compliant"
// rule). The CASE verdict is taken verbatim from evaluateControl().status so the
// assembler can never disagree with the live control status shown elsewhere.
//
// ── Where the A1 evidence nodes attach ────────────────────────────────────────
// selectEvidenceNodes() surfaces five node kinds. Each maps to exactly one
// sub-claim, chosen so the argument reads coherently AND so each node's own
// freshness clock lines up with the sub-claim it informs:
//
//   approved-document        → designed     (the documented, current policy; its
//                                            freshness clock IS the Gate 2 review SLA,
//                                            so a lapsing policy surfaces under designed)
//   verified-artefact        → implemented  (operational proof the control is in place)
//   completed-risk-action    → implemented
//   satisfied-risk-obligation→ implemented
//   fulfilled-obligation     → operating    (Gate 5 obligations current)
//
// The effectiveness rating is added to `operating`; the monitoring signals are
// derived here and added to `monitored`.
//
// Pure and read-time: no state keys, no persistence, no side effects.

import { evaluateControl } from '../evaluateControl.js';
import { selectEvidenceNodes } from './evidenceNodes.js';
import { getEquivalentControls } from '../../constants/controlMappings.js';
import { EVIDENCE_STATE, CONFIDENCE } from './nodeClassification.js';
import { PREDICTED_STATUS, predictNodeDecay } from './nodePrediction.js';
import {
  getEffectivenessLevel,
  effectivenessLastLookedAt,
  effectivenessStaleAfterMs,
  isEffectivenessStale,
  EFFECTIVENESS_REVIEW_PERIOD_DAYS,
} from '../../constants/effectivenessScale.js';
import { buildRequirementIndex } from '../../utils/requirementRatings.js';

const DAY_MS = 24 * 60 * 60 * 1000;

/** The four lifecycle legs of the assurance argument. */
export const SUB_CLAIM_KEYS = Object.freeze({
  DESIGNED: 'designed',
  IMPLEMENTED: 'implemented',
  OPERATING: 'operating',
  MONITORED: 'monitored',
});

/**
 * Assurance verdict vocabulary. The first three mirror evaluateControl's
 * compliant/partial/non-compliant semantics exactly. `not-evidenced` is the
 * honest fourth value used ONLY when a sub-claim has no evidence available in
 * the current context (e.g. a control with no integrations) — it is never a
 * silent pass and never drags the case verdict down.
 */
export const ASSURANCE_VERDICT = Object.freeze({
  COMPLIANT: 'compliant',
  PARTIAL: 'partial',
  NON_COMPLIANT: 'non-compliant',
  NOT_EVIDENCED: 'not-evidenced',
});

/** Node kinds this assembler synthesises (beyond the five A1 kinds). */
export const ASSURANCE_NODE_KINDS = Object.freeze({
  EFFECTIVENESS_RATING: 'effectiveness-rating',
  INTEGRATION_FRESHNESS: 'integration-freshness',
  ENGINE_RUN_LOG: 'engine-run-log',
});

// A1 evidence-node kind → the sub-claim it attaches to. Deterministic by kind so
// a shared node lands under the same sub-claim in every control's case (reuse
// invariant, tested).
const NODE_KIND_TO_SUB_CLAIM = Object.freeze({
  'approved-document': SUB_CLAIM_KEYS.DESIGNED,
  'verified-artefact': SUB_CLAIM_KEYS.IMPLEMENTED,
  'completed-risk-action': SUB_CLAIM_KEYS.IMPLEMENTED,
  'satisfied-risk-obligation': SUB_CLAIM_KEYS.IMPLEMENTED,
  'fulfilled-obligation': SUB_CLAIM_KEYS.OPERATING,
});

// Canonical order — most fundamental leg first. Used when picking the single
// weakest link and when laying the sub-claims out.
const SUB_CLAIM_ORDER = [
  SUB_CLAIM_KEYS.DESIGNED,
  SUB_CLAIM_KEYS.IMPLEMENTED,
  SUB_CLAIM_KEYS.OPERATING,
  SUB_CLAIM_KEYS.MONITORED,
];

const SUB_CLAIM_LABEL = {
  [SUB_CLAIM_KEYS.DESIGNED]: 'Designed',
  [SUB_CLAIM_KEYS.IMPLEMENTED]: 'Implemented',
  [SUB_CLAIM_KEYS.OPERATING]: 'Operating',
  [SUB_CLAIM_KEYS.MONITORED]: 'Monitored',
};

/** Resolve an instant to ms, falling back to now on null/invalid input. */
function resolveNowMs(asOf) {
  if (asOf == null) return Date.now();
  const m = typeof asOf === 'number' ? asOf : new Date(asOf).getTime();
  return Number.isFinite(m) ? m : Date.now();
}

/** Parse a poll interval like '15m' / '1h' / '2d' to ms, or null. */
function parseIntervalMs(raw) {
  if (typeof raw !== 'string') return null;
  const m = raw.trim().match(/^(\d+)\s*([mhd])$/i);
  if (!m) return null;
  const n = Number(m[1]);
  const unit = m[2].toLowerCase();
  return unit === 'm' ? n * 60 * 1000 : unit === 'h' ? n * 60 * 60 * 1000 : n * DAY_MS;
}

/**
 * Roll a set of owning gates up to a sub-claim verdict, mirroring the existing
 * pass/blocked semantics. `overdueHardFail` reproduces evaluateControl's rule
 * that any overdue obligation forces non-compliant regardless of pass count.
 */
function verdictFromGates(gateList, { overdueHardFail = false } = {}) {
  const gates = gateList.filter(Boolean);
  if (gates.length === 0) return null;
  if (overdueHardFail) return ASSURANCE_VERDICT.NON_COMPLIANT;
  const passed = gates.filter((g) => g.pass).length;
  if (passed === gates.length) return ASSURANCE_VERDICT.COMPLIANT;
  if (passed === 0) return ASSURANCE_VERDICT.NON_COMPLIANT;
  return ASSURANCE_VERDICT.PARTIAL;
}

/** Nodes in this sub-claim that are green today but decay on/before the audit. */
function collectPredictedWeakLinks(nodes) {
  return nodes
    .filter((n) => n.predicted?.status === PREDICTED_STATUS.DECAYS_BEFORE_AUDIT)
    .map((n) => ({
      id: n.id,
      kind: n.kind,
      label: n.label,
      decayDate: n.predicted.decayDate,
      fixWindowDays: n.predicted.fixWindowDays,
    }));
}

/**
 * Build a gate-backed sub-claim (designed / implemented / operating).
 */
function buildGateSubClaim(
  key,
  gates,
  nodes,
  { overdueHardFail = false, extraReason = null } = {}
) {
  const owning = gates.filter(Boolean);
  const verdict = verdictFromGates(owning, { overdueHardFail });
  const failing = owning.filter((g) => !g.pass);

  const reasonParts = failing.map((g) => g.reason).filter(Boolean);
  if (extraReason) reasonParts.push(extraReason);
  const reason =
    reasonParts.length > 0
      ? reasonParts.join('; ')
      : owning
          .map((g) => g.reason)
          .filter(Boolean)
          .join('; ') || 'No gate evidence for this leg';

  const weakLinks = collectPredictedWeakLinks(nodes);
  return {
    key,
    label: SUB_CLAIM_LABEL[key],
    verdict,
    reason,
    gates: owning.map((g) => g.id),
    evidence: nodes,
    predicted: {
      // "amber" = passes/holds today but a node decays before the audit date.
      // Only meaningful when the leg isn't already failing outright.
      amber: verdict !== ASSURANCE_VERDICT.NON_COMPLIANT && weakLinks.length > 0,
      weakLinks,
    },
  };
}

/**
 * Build the monitored sub-claim from the derived monitoring nodes.
 * Honest by construction: with no monitoring context it is `not-evidenced`
 * (never a silent pass); with monitoring that is all broken it is non-compliant.
 */
function buildMonitoredSubClaim(nodes, hasContext) {
  let verdict;
  let reason;
  if (!hasContext) {
    verdict = ASSURANCE_VERDICT.NOT_EVIDENCED;
    reason = 'No continuous-monitoring signals available in this context';
  } else if (nodes.length === 0) {
    verdict = ASSURANCE_VERDICT.NOT_EVIDENCED;
    reason = 'No connected integrations or engine runs to evidence continuous monitoring';
  } else {
    const allBroken = nodes.every((n) => n.predicted?.status === PREDICTED_STATUS.OBSERVED_FAIL);
    const anyHealthy = nodes.some(
      (n) =>
        n.predicted?.status !== PREDICTED_STATUS.OBSERVED_FAIL && n.confidence !== CONFIDENCE.LOW
    );
    if (allBroken) {
      verdict = ASSURANCE_VERDICT.NON_COMPLIANT;
      reason = 'Monitoring signals present but every one is stale or failing';
    } else if (anyHealthy) {
      verdict = ASSURANCE_VERDICT.COMPLIANT;
      reason = 'Live continuous-monitoring signals present (ISO 27001:2022 §9.1)';
    } else {
      verdict = ASSURANCE_VERDICT.PARTIAL;
      reason = 'Monitoring signals present but stale or low-confidence';
    }
  }

  const weakLinks = collectPredictedWeakLinks(nodes);
  return {
    key: SUB_CLAIM_KEYS.MONITORED,
    label: SUB_CLAIM_LABEL[SUB_CLAIM_KEYS.MONITORED],
    verdict,
    reason,
    gates: [],
    evidence: nodes,
    predicted: {
      amber: verdict !== ASSURANCE_VERDICT.NON_COMPLIANT && weakLinks.length > 0,
      weakLinks,
    },
  };
}

/**
 * Requirement keys per TCF control, cached against the frameworks array so a
 * whole-catalogue assurance pass indexes once rather than per control.
 */
const REQUIREMENT_KEY_CACHE = new WeakMap();

function requirementKeysFor(tcfId, frameworks) {
  if (!Array.isArray(frameworks) || !tcfId) return [];
  let byScfId = REQUIREMENT_KEY_CACHE.get(frameworks);
  if (!byScfId) {
    byScfId = buildRequirementIndex(frameworks).keysByScfId;
    REQUIREMENT_KEY_CACHE.set(frameworks, byScfId);
  }
  return byScfId.get(tcfId) || [];
}

/**
 * The effectiveness-rating evidence node (ISO 27001:2022 §9.1) for a control,
 * or null when unrated.
 *
 * A rating belongs to a framework REQUIREMENT (CTRL-B-001), and one control
 * can answer several — which may be rated differently, and by different
 * people. The assurance case is one claim about the control, so it takes the
 * STRICTEST of them: a claim assembled from a merge resolves to the lower of
 * the two, never the average or the most flattering. `backingRefs.requirement`
 * names which one it came from, so an auditor can see the clause behind the
 * figure rather than the merged control.
 *
 * The two pre-requirement key shapes are still read as a fallback, in that
 * order, so a tenant that has not re-rated anything keeps the evidence it
 * already had — on the same clock as everything else.
 *
 * ── Confidence (GH #7168) ────────────────────────────────────────────────────
 *
 * A rating is a person's self-assessment, so it is never HIGH: machine-observed
 * integration freshness is MEDIUM, and a self-attested score does not outrank
 * it (TRYGGR_INTENT.md §5). While every rated requirement of the control has
 * been looked at within EFFECTIVENESS_REVIEW_PERIOD_DAYS it is MEDIUM; once
 * any of them lapses — or carries no readable date — it is LOW. Freshness runs
 * off the OLDEST requirement, not the strictest-scored one: a higher rating
 * nobody has revisited could be hiding a lower truth. `backingRefs.lastLookedAt`
 * and `staleRequirement` name that date and clause, so the reason an auditor
 * reads matches the refs beside it. The clock only ever lowers confidence.
 * The staleness rule is the one the maturity screen uses (effectivenessScale.js).
 */
function buildEffectivenessNode(
  controlId,
  tcfId,
  controlEffectiveness,
  frameworks,
  nowMs,
  auditDate
) {
  if (!controlEffectiveness) return null;
  const perRequirement = requirementKeysFor(tcfId, frameworks)
    .map((key) => ({ key, entry: controlEffectiveness[key] }))
    .filter((r) => r.entry?.score >= 1 && r.entry?.score <= 5)
    .sort((a, b) => a.entry.score - b.entry.score);
  const strictest = perRequirement[0] || null;
  const rating = strictest?.entry || controlEffectiveness[tcfId] || controlEffectiveness[controlId];
  if (!rating || !(rating.score >= 1 && rating.score <= 5)) return null;
  const level = getEffectivenessLevel(rating.score);

  // The rating whose review clock lapses first. An undated one sorts first.
  const clocked = perRequirement.length ? perRequirement : [{ key: null, entry: rating }];
  const oldest = clocked.reduce((a, b) => {
    const av = effectivenessStaleAfterMs(a.entry);
    const bv = effectivenessStaleAfterMs(b.entry);
    const an = Number.isFinite(av) ? av : -Infinity;
    const bn = Number.isFinite(bv) ? bv : -Infinity;
    return bn < an ? b : a;
  });
  const lastLookedAt = effectivenessLastLookedAt(oldest.entry);
  const stale = isEffectivenessStale(oldest.entry, nowMs);

  const node = {
    id: `${ASSURANCE_NODE_KINDS.EFFECTIVENESS_RATING}:${tcfId}`,
    kind: ASSURANCE_NODE_KINDS.EFFECTIVENESS_RATING,
    label: `Effectiveness: ${level ? level.short : rating.score}`,
    sourceRef: tcfId,
    gate: 'effectiveness',
    backingRefs: {
      score: rating.score,
      justification: rating.justification || null,
      ratedAt: rating.ratedAt || null,
      ratedBy: rating.ratedBy || null,
      // GH #5643: the last §9.1 review that confirmed this rating without
      // changing it. `ratedAt` answers "when did this rating last change";
      // an auditor asking "when did you last review this control's
      // effectiveness" is asking for the later of the two.
      reviewedAt: rating.reviewedAt || null,
      reviewedBy: rating.reviewedBy || null,
      requirement: strictest?.key || null,
      ratedRequirements: perRequirement.length || null,
      // GH #7168: the review clock — the least recently looked-at rating.
      lastLookedAt,
      staleRequirement: stale ? oldest.key : null,
      reviewPeriodDays: EFFECTIVENESS_REVIEW_PERIOD_DAYS,
    },
    alsoServes: null,
    state: EVIDENCE_STATE.OBSERVED,
    confidence: stale ? CONFIDENCE.LOW : CONFIDENCE.MEDIUM,
    confidenceReason: stale
      ? lastLookedAt
        ? `Self-assessed effectiveness rating (ISO 27001:2022 §9.1) not reviewed within its ${EFFECTIVENESS_REVIEW_PERIOD_DAYS}-day review period — last looked at ${lastLookedAt.slice(0, 10)}.`
        : 'Self-assessed effectiveness rating (ISO 27001:2022 §9.1) with no recorded rating or review date.'
      : `Self-assessed effectiveness rating (ISO 27001:2022 §9.1) with justification, reviewed within its ${EFFECTIVENESS_REVIEW_PERIOD_DAYS}-day review period.`,
  };
  node.predicted = predictNodeDecay(node, null, nowMs, auditDate);
  return node;
}

/**
 * Derive the monitoring evidence nodes for the monitored sub-claim:
 *   • one integration-freshness node per connected+enabled integration, and
 *   • one engine-run-log presence node when scheduled monitoring jobs have run.
 *
 * These are tenant-wide continuous-monitoring signals (ISO 27001:2022 §9.1)
 * attached to every control's monitored leg. This REFERENCES the existing
 * integration `lastSync` fields and the engine_run_log — it does not re-run or
 * rebuild either. An integration that is not connected produces no node (an
 * honest gap), never a silent pass.
 */
function buildMonitoringNodes(domainContext, nowMs) {
  const nodes = [];
  const integrations = domainContext?.integrations;
  if (integrations && typeof integrations === 'object') {
    for (const [id, cfg] of Object.entries(integrations)) {
      if (!cfg || cfg.enabled === false) continue;
      const lastSyncMs = cfg.lastSync ? new Date(cfg.lastSync).getTime() : NaN;
      const connected = !!cfg.connectedAt || Number.isFinite(lastSyncMs);
      if (!connected) continue; // not connected → not monitoring evidence
      const intervalMs = parseIntervalMs(cfg.pollInterval) || DAY_MS;
      const staleThreshold = Math.max(intervalMs * 2, DAY_MS);
      const stale = !Number.isFinite(lastSyncMs) || nowMs - lastSyncMs > staleThreshold;
      const failed =
        cfg.lastSyncStatus && cfg.lastSyncStatus !== 'ok' && cfg.lastSyncStatus !== 'success';
      const healthy = !stale && !failed;
      nodes.push({
        id: `${ASSURANCE_NODE_KINDS.INTEGRATION_FRESHNESS}:${id}`,
        kind: ASSURANCE_NODE_KINDS.INTEGRATION_FRESHNESS,
        label: `${id} integration ${healthy ? 'syncing' : failed ? 'sync failing' : 'sync stale'}`,
        sourceRef: id,
        gate: 'monitored',
        backingRefs: {
          integrationId: id,
          lastSync: cfg.lastSync || null,
          lastSyncStatus: cfg.lastSyncStatus || null,
          pollInterval: cfg.pollInterval || null,
        },
        alsoServes: null,
        state: EVIDENCE_STATE.INFERRED, // freshness is a time-derived signal
        confidence: healthy ? CONFIDENCE.MEDIUM : CONFIDENCE.LOW,
        confidenceReason: healthy
          ? 'Integration last-sync is within its expected cadence — continuous monitoring is live.'
          : failed
            ? 'Integration last sync did not complete successfully.'
            : 'Integration sync is stale — last sync is older than twice its poll interval.',
        predicted: {
          status: healthy ? PREDICTED_STATUS.FRESH_THROUGH_AUDIT : PREDICTED_STATUS.OBSERVED_FAIL,
          decayDate: Number.isFinite(lastSyncMs)
            ? new Date(lastSyncMs + staleThreshold).toISOString()
            : null,
          fixWindowDays: null,
        },
      });
    }
  }

  const runLog = domainContext?.engineRunLog;
  const runCount = Array.isArray(runLog)
    ? runLog.length
    : typeof domainContext?.engineRunCount === 'number'
      ? domainContext.engineRunCount
      : 0;
  if (runCount > 0) {
    let latestMs = NaN;
    if (Array.isArray(runLog)) {
      for (const r of runLog) {
        const t = new Date(r?.startedAt || r?.completedAt || r?.ranAt).getTime();
        if (Number.isFinite(t) && (!Number.isFinite(latestMs) || t > latestMs)) latestMs = t;
      }
    }
    const stale = Number.isFinite(latestMs) ? nowMs - latestMs > 2 * DAY_MS : false;
    nodes.push({
      id: `${ASSURANCE_NODE_KINDS.ENGINE_RUN_LOG}:presence`,
      kind: ASSURANCE_NODE_KINDS.ENGINE_RUN_LOG,
      label: `Continuous-monitoring engine runs recorded (${runCount})`,
      sourceRef: 'engine_run_log',
      gate: 'monitored',
      backingRefs: {
        runCount,
        latestRunAt: Number.isFinite(latestMs) ? new Date(latestMs).toISOString() : null,
      },
      alsoServes: null,
      state: EVIDENCE_STATE.OBSERVED,
      confidence: stale ? CONFIDENCE.LOW : CONFIDENCE.HIGH,
      confidenceReason: stale
        ? 'Engine run-log entries exist but the most recent run is over 48h old.'
        : 'Automation-engine run-log entries evidence scheduled monitoring jobs executed (ISO 27001:2022 §9.1).',
      predicted: { status: PREDICTED_STATUS.NO_CLOCK, decayDate: null, fixWindowDays: null },
    });
  }

  return nodes;
}

/**
 * The single most pressing weak point of the whole argument. Severity order:
 *   1. a sub-claim failing now (non-compliant)
 *   2. a sub-claim partially holding
 *   3. the soonest node that decays before the audit date
 *   4. an un-evidenced sub-claim (an honest gap)
 *   5. else null (fully assured through the audit)
 */
function computeWeakestLink(subClaims, predictedWeakLinks) {
  const byKey = new Map(subClaims.map((s) => [s.key, s]));
  const ordered = SUB_CLAIM_ORDER.map((k) => byKey.get(k)).filter(Boolean);

  const failing = ordered.find((s) => s.verdict === ASSURANCE_VERDICT.NON_COMPLIANT);
  if (failing) {
    return {
      subClaim: failing.key,
      label: failing.label,
      status: 'failing-now',
      reason: failing.reason,
    };
  }

  const partial = ordered.find((s) => s.verdict === ASSURANCE_VERDICT.PARTIAL);
  if (partial) {
    return {
      subClaim: partial.key,
      label: partial.label,
      status: 'failing-now',
      reason: partial.reason,
    };
  }

  if (predictedWeakLinks.length > 0) {
    const soonest = predictedWeakLinks[0];
    return {
      subClaim: soonest.subClaim,
      label: soonest.label,
      status: 'decays-before-audit',
      reason: `Decays on ${soonest.decayDate} — ${soonest.fixWindowDays} day fix-window`,
      decayDate: soonest.decayDate,
      fixWindowDays: soonest.fixWindowDays,
    };
  }

  const notEvidenced = ordered.find((s) => s.verdict === ASSURANCE_VERDICT.NOT_EVIDENCED);
  if (notEvidenced) {
    return {
      subClaim: notEvidenced.key,
      label: notEvidenced.label,
      status: 'not-evidenced',
      reason: notEvidenced.reason,
    };
  }

  return null;
}

/**
 * Assemble a control's ControlAssuranceCase.
 *
 * Pure and read-only: it calls evaluateControl() (single source of truth for
 * gate status and the case verdict) and selectEvidenceNodes() (A1–A3 nodes with
 * state/confidence/predicted), then composes them into a stated argument.
 *
 * @param {string} controlId
 * @param {Array}  documents      tenant documents (same array passed to evaluateControl)
 * @param {Object} domainContext  obligations/artefacts/risks/… plus optional
 *                                 controlEffectiveness, integrations, engineRunLog
 * @param {Object} [options]
 * @param {number|Date|string} [options.asOf]      evaluation instant (forwarded)
 * @param {number|Date|string} [options.auditDate] forecast target for predicted decay
 * @returns {{controlId, goal, verdict, strategy, subClaims, weakestLink, predictedWeakLinks}}
 */
export function assembleControlAssuranceCase(controlId, documents, domainContext, options = {}) {
  const { asOf, auditDate } = options;
  const nowMs = resolveNowMs(asOf);

  // Authoritative gate evaluation + case verdict — never recomputed here.
  const evaluation = evaluateControl(controlId, documents, domainContext, asOf);
  const gateById = new Map((evaluation.gates || []).map((g) => [g.id, g]));
  const gate = (id) => gateById.get(id) || null;

  // A1–A3 evidence nodes (already carry state / confidence / predicted).
  const nodes = selectEvidenceNodes(controlId, documents, domainContext, asOf, auditDate);

  // Partition A1 nodes into their sub-claims by kind (deterministic → reuse-safe).
  const bucket = {
    [SUB_CLAIM_KEYS.DESIGNED]: [],
    [SUB_CLAIM_KEYS.IMPLEMENTED]: [],
    [SUB_CLAIM_KEYS.OPERATING]: [],
    [SUB_CLAIM_KEYS.MONITORED]: [],
  };
  for (const n of nodes) {
    const key = NODE_KIND_TO_SUB_CLAIM[n.kind];
    if (key) bucket[key].push(n);
  }

  // Effectiveness rating (ISO §9.1) → operating.
  const tcfId = getEquivalentControls(controlId)[0];
  const effNode = buildEffectivenessNode(
    controlId,
    tcfId,
    domainContext?.controlEffectiveness,
    domainContext?.frameworks,
    nowMs,
    auditDate
  );
  if (effNode) bucket[SUB_CLAIM_KEYS.OPERATING].push(effNode);

  // Monitoring signals → monitored.
  const monitoringNodes = buildMonitoringNodes(domainContext, nowMs);
  bucket[SUB_CLAIM_KEYS.MONITORED].push(...monitoringNodes);
  const hasMonitoringContext = !!(
    domainContext &&
    (domainContext.integrations ||
      domainContext.engineRunLog ||
      typeof domainContext.engineRunCount === 'number')
  );

  const ownershipGate = gate('ownership'); // present only when Gate 6 is enabled
  const obligationsGate = gate('obligations');
  const overdueHardFail = (obligationsGate?.meta?.overdueObligations || []).length > 0;

  // Effectiveness reason line for the operating leg (informational — the sub-claim
  // verdict is gate-driven; §9.1 maturity is separate from the binary gates).
  let operatingExtraReason = null;
  if (effNode) {
    operatingExtraReason = `Effectiveness rated ${effNode.backingRefs.score}/5`;
  } else {
    operatingExtraReason = 'Effectiveness not yet rated (ISO 27001:2022 §9.1)';
  }

  const subClaims = [
    buildGateSubClaim(
      SUB_CLAIM_KEYS.DESIGNED,
      [gate('document'), gate('review')],
      bucket[SUB_CLAIM_KEYS.DESIGNED]
    ),
    buildGateSubClaim(
      SUB_CLAIM_KEYS.IMPLEMENTED,
      ownershipGate ? [gate('evidence'), ownershipGate] : [gate('evidence')],
      bucket[SUB_CLAIM_KEYS.IMPLEMENTED]
    ),
    buildGateSubClaim(
      SUB_CLAIM_KEYS.OPERATING,
      [obligationsGate, gate('read')],
      bucket[SUB_CLAIM_KEYS.OPERATING],
      { overdueHardFail, extraReason: operatingExtraReason }
    ),
    buildMonitoredSubClaim(bucket[SUB_CLAIM_KEYS.MONITORED], hasMonitoringContext),
  ];

  // Case-level predicted weak links (all legs), soonest fix-window first.
  const predictedWeakLinks = subClaims
    .flatMap((s) => s.predicted.weakLinks.map((w) => ({ ...w, subClaim: s.key })))
    .sort((a, b) => {
      const av = a.fixWindowDays ?? Infinity;
      const bv = b.fixWindowDays ?? Infinity;
      return av - bv;
    });

  const weakestLink = computeWeakestLink(subClaims, predictedWeakLinks);

  return {
    controlId,
    goal: `Control ${controlId} is effective: designed, implemented, operating, and monitored.`,
    verdict: evaluation.status,
    strategy:
      'Argue the control across its lifecycle — Designed (documented and reviewed), ' +
      'Implemented (evidence and ownership in place), Operating (obligations current, readers ' +
      'acknowledged, effectiveness rated), and Monitored (continuous-monitoring signals) — ' +
      'rolling each leg up from the compliance gates, obligation currency, effectiveness rating, ' +
      'and monitoring evidence.',
    subClaims,
    weakestLink,
    predictedWeakLinks,
  };
}
